import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type ReviewTeacherStatus = 'candidate' | 'training' | 'working'

export interface TrainerReviewTeacherOption {
  teacherId: string
  fullName: string
  status: ReviewTeacherStatus
  partnerNumbers: number[]
  citiesText: string
  matchedAliases?: string[]
  hasOtherAliases?: boolean
}

export interface TeacherQualityReview {
  reviewId: string
  reviewDate: string
  trainingAt: string | null
  cityName: string
  studioAddress: string
  directionId: number | null
  directionName: string
  totalScore: number
  maxScore: number
  qualityPercent: number
  conclusion: string
  reviewerUserId: string | null
  reviewerName: string
}

export interface TeacherQualitySnapshot {
  teacherId: string
  dateFrom: string | null
  dateTo: string | null
  reviewsCount: number
  averageQualityPercent: number | null
  latestQualityPercent: number | null
  latestReviewDate: string | null
  trendDelta: number | null
  reviews: TeacherQualityReview[]
}

export interface TeacherQualityMetric {
  teacherId: string
  reviewsCount: number
  averageQualityPercent: number | null
  latestQualityPercent: number | null
  latestReviewDate: string | null
  trendDelta: number | null
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value)
}

function normalizeNullableText(value: unknown): string | null {
  const text = normalizeText(value).trim()
  return text || null
}

function toNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function toNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}


function toReviewId(value: string): number {
  const parsed = Number(value)
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new Error('Некорректный идентификатор отсмотра')
  }
  return parsed
}

function formatError(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message || 'Неизвестная ошибка')
  }
  return String(error || 'Неизвестная ошибка')
}

function mapTeacherOption(row: Record<string, unknown>): TrainerReviewTeacherOption {
  return {
    teacherId: normalizeText(row.teacher_id),
    fullName: normalizeText(row.full_name),
    status: row.status as ReviewTeacherStatus,
    partnerNumbers: Array.isArray(row.partner_numbers)
      ? row.partner_numbers.map(toNumber).filter(value => value > 0)
      : [],
    citiesText: normalizeText(row.cities_text),
    matchedAliases: Array.isArray(row.matched_aliases)
      ? row.matched_aliases.map(normalizeText).filter(Boolean)
      : [],
    hasOtherAliases: Boolean(row.has_other_aliases),
  }
}

function mapReview(row: Record<string, unknown>): TeacherQualityReview {
  return {
    reviewId: normalizeText(row.reviewId ?? row.review_id),
    reviewDate: normalizeText(row.reviewDate ?? row.review_date),
    trainingAt: normalizeNullableText(row.trainingAt ?? row.training_at),
    cityName: normalizeText(row.cityName ?? row.city_name),
    studioAddress: normalizeText(row.studioAddress ?? row.studio_address),
    directionId: toNullableNumber(row.directionId ?? row.direction_id),
    directionName: normalizeText(row.directionName ?? row.direction_name),
    totalScore: toNumber(row.totalScore ?? row.total_score),
    maxScore: toNumber(row.maxScore ?? row.max_score),
    qualityPercent: toNumber(row.qualityPercent ?? row.quality_percent),
    conclusion: normalizeText(row.conclusion),
    reviewerUserId: normalizeNullableText(row.reviewerUserId ?? row.reviewer_user_id),
    reviewerName: normalizeText(row.reviewerName ?? row.reviewer_name),
  }
}

export async function listTrainerReviewTeacherOptions(
  studioInternalId: number | null,
  search = ''
): Promise<TrainerReviewTeacherOption[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_trainer_review_teacher_options', {
      p_studio_internal_id: studioInternalId,
      p_search: search.trim() || null,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapTeacherOption)
}

export async function getTrainerReviewTeacherLink(
  reviewId: string
): Promise<TrainerReviewTeacherOption | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_trainer_review_teacher_link', {
      p_review_id: toReviewId(reviewId),
    })
  )

  if (error) throw new Error(formatError(error))
  const row = Array.isArray(data) ? data[0] : data
  return row ? mapTeacherOption(row as Record<string, unknown>) : null
}

export async function getTeacherQualitySnapshot(
  teacherId: string,
  dateFrom: string | null = null,
  dateTo: string | null = null
): Promise<TeacherQualitySnapshot> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_franchise_teacher_quality_snapshot', {
      p_teacher_id: teacherId,
      p_date_from: dateFrom,
      p_date_to: dateTo,
    })
  )

  if (error) throw new Error(formatError(error))

  const row = (data ?? {}) as Record<string, unknown>
  const reviews = Array.isArray(row.reviews)
    ? row.reviews.map(item => mapReview(item as Record<string, unknown>))
    : []

  return {
    teacherId: normalizeText(row.teacherId ?? row.teacher_id ?? teacherId),
    dateFrom: normalizeNullableText(row.dateFrom ?? row.date_from),
    dateTo: normalizeNullableText(row.dateTo ?? row.date_to),
    reviewsCount: toNumber(row.reviewsCount ?? row.reviews_count),
    averageQualityPercent: toNullableNumber(row.averageQualityPercent ?? row.average_quality_percent),
    latestQualityPercent: toNullableNumber(row.latestQualityPercent ?? row.latest_quality_percent),
    latestReviewDate: normalizeNullableText(row.latestReviewDate ?? row.latest_review_date),
    trendDelta: toNullableNumber(row.trendDelta ?? row.trend_delta),
    reviews,
  }
}

export async function listTeacherQualityMetrics(
  dateFrom: string | null = null,
  dateTo: string | null = null
): Promise<TeacherQualityMetric[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_franchise_teacher_quality_metrics', {
      p_date_from: dateFrom,
      p_date_to: dateTo,
    })
  )

  if (error) throw new Error(formatError(error))

  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    teacherId: normalizeText(row.teacher_id),
    reviewsCount: toNumber(row.reviews_count),
    averageQualityPercent: toNullableNumber(row.average_quality_percent),
    latestQualityPercent: toNullableNumber(row.latest_quality_percent),
    latestReviewDate: normalizeNullableText(row.latest_review_date),
    trendDelta: toNullableNumber(row.trend_delta),
  }))
}
